import React, { useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface Props {
  open: boolean;
  playerName: string;
  playerIndex: number;    // 0-based index del giocatore corrente nella callup
  totalPlayers: number;   // lunghezza della callup
  currentNumber: number | null;
  takenNumbers: Set<number>;
  onSelect: (n: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}

const GRID_SIZE = 10;
const TOTAL = 100;

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 0.6,
        py: 0.1,
        fontSize: 9,
        lineHeight: 1.4,
        fontFamily: 'monospace',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 0.5,
        bgcolor: 'action.hover',
        color: 'text.secondary',
        userSelect: 'none',
      }}
    >
      {children}
    </Box>
  );
}

export default function ShirtNumberPicker({
  open,
  playerName,
  playerIndex,
  totalPlayers,
  currentNumber,
  takenNumbers,
  onSelect,
  onPrev,
  onNext,
  onClose,
}: Props) {
  useEffect(() => {
    if (!open || totalPlayers <= 1) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); onPrev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); onNext(); }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, totalPlayers, onPrev, onNext]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6, pb: 0.5 }}>
        Numero maglia
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Navigazione giocatore */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <IconButton size="small" onClick={onPrev} disabled={totalPlayers <= 1}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          {totalPlayers > 1 && <Kbd>←</Kbd>}
        </Box>

        <Box sx={{ textAlign: 'center', flex: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold" noWrap>
            {playerName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {playerIndex + 1} / {totalPlayers}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <IconButton size="small" onClick={onNext} disabled={totalPlayers <= 1}>
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
          {totalPlayers > 1 && <Kbd>→</Kbd>}
        </Box>
      </Box>

      <DialogContent sx={{ pt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
          Tocca un numero per assegnarlo. I numeri già usati da altri convocati sono disabilitati.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
            gap: 0.5,
          }}
        >
          {Array.from({ length: TOTAL }, (_, i) => i + 1).map((n) => {
            const isCurrent = n === currentNumber;
            const isTaken = takenNumbers.has(n) && !isCurrent;

            return (
              <Box
                key={n}
                onClick={() => !isTaken && onSelect(n)}
                sx={{
                  aspectRatio: '1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 0.75,
                  fontSize: 12,
                  fontWeight: isCurrent ? 'bold' : 'normal',
                  cursor: isTaken ? 'not-allowed' : 'pointer',
                  border: '1px solid',
                  borderColor: isCurrent ? 'primary.main' : 'divider',
                  bgcolor: isCurrent
                    ? 'primary.main'
                    : isTaken
                    ? 'action.disabledBackground'
                    : 'background.paper',
                  color: isCurrent
                    ? 'primary.contrastText'
                    : isTaken
                    ? 'text.disabled'
                    : 'text.primary',
                  '&:hover': !isTaken
                    ? { bgcolor: isCurrent ? 'primary.dark' : 'action.hover' }
                    : undefined,
                  transition: 'background-color 0.15s',
                }}
              >
                {n}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
